export const articleShippingCost = {
  slug: 'usfans-shipping-cost',
  en: {
    title: 'USFans Shipping Cost: A Complete Guide to Delivery',
    description:
      'Understand the factors that influence USFans shipping cost, from destination and weight to method and extra services.',
    tag: 'Shipping',
    intro: [
      'Shipping cost is one of the first things people want to know before ordering. The exact price usually depends on several factors, so a fixed number without a current source can be misleading.',
      'This guide explains what affects delivery cost and how to think about it, so you can plan more accurately and avoid surprises at checkout.',
    ],
    sections: [
      {
        title: 'Factors that affect shipping cost',
        paragraphs: [
          'Destination, package weight, package size and the chosen shipping method are the biggest drivers of cost. Extra services such as faster delivery or insurance can also change the total.',
          'Because these factors combine, two orders that look similar can have very different delivery prices.',
        ],
      },
      {
        title: 'How to estimate your cost',
        paragraphs: [
          'The most reliable way is to check the shipping options shown at checkout for your specific country and package. Use any available calculator rather than relying on old or copied numbers.',
          'Keep in mind that international orders may include additional fees or customs charges that are not part of the base shipping price.',
        ],
      },
      {
        title: 'Delivery time vs cost',
        paragraphs: [
          'Faster methods usually cost more, while standard options are often cheaper but slower. Choose based on how quickly you need the package.',
          'Always confirm the estimated delivery time for your region before finalizing an order.',
        ],
      },
    ],
    faq: [
      { q: 'What affects USFans shipping cost?', a: 'Destination, weight, package size, shipping method and any extra services all influence the final price.' },
      { q: 'Is shipping the same for every country?', a: 'No. Costs and delivery times vary by destination, so always check the options for your specific country.' },
      { q: 'Where can I find the exact cost?', a: 'Check the shipping options shown at checkout for your order, or use any available calculator rather than relying on copied numbers.' },
    ],
  },
  pl: {
    title: 'USFans Shipping Cost: kompletny przewodnik po dostawie',
    description:
      'Poznaj czynniki wpływające na koszt wysyłki USFans: od kraju docelowego i wagi po metodę i usługi dodatkowe.',
    tag: 'Wysyłka',
    intro: [
      'Koszt wysyłki to jedna z pierwszych rzeczy, które chcemy poznać przed zamówieniem. Dokładna cena zwykle zależy od kilku czynników, więc stała kwota bez aktualnego źródła może wprowadzać w błąd.',
      'Ten przewodnik wyjaśnia, co wpływa na koszt dostawy i jak o nim myśleć, aby lepiej planować i unikać niespodzianek przy kasie.',
    ],
    sections: [
      {
        title: 'Czynniki wpływające na koszt wysyłki',
        paragraphs: [
          'Kraj docelowy, waga paczki, jej wymiary oraz wybrana metoda wysyłki to najważniejsze czynniki. Usługi dodatkowe, takie jak szybsza dostawa lub ubezpieczenie, również zmieniają sumę.',
          'Ponieważ czynniki te się łączą, dwa podobnie wyglądające zamówienia mogą mieć bardzo różne ceny dostawy.',
        ],
      },
      {
        title: 'Jak oszacować koszt',
        paragraphs: [
          'Najpewniejszym sposobem jest sprawdzenie opcji wysyłki pokazywanych przy kasie dla Twojego kraju i paczki. Korzystaj z dostępnego kalkulatora zamiast starych lub skopiowanych kwot.',
          'Pamiętaj, że zamówienia międzynarodowe mogą obejmować dodatkowe opłaty lub cła, które nie wchodzą w podstawową cenę wysyłki.',
        ],
      },
      {
        title: 'Czas dostawy a koszt',
        paragraphs: [
          'Szybsze metody zwykle kosztują więcej, a standardowe opcje są często tańsze, ale wolniejsze. Wybierz w zależności od tego, jak szybko potrzebujesz paczki.',
          'Zawsze potwierdź szacowany czas dostawy dla swojego regionu przed sfinalizowaniem zamówienia.',
        ],
      },
    ],
    faq: [
      { q: 'Co wpływa na koszt wysyłki USFans?', a: 'Kraj docelowy, waga, wymiary paczki, metoda wysyłki i usługi dodatkowe wpływają na ostateczną cenę.' },
      { q: 'Czy wysyłka jest taka sama w każdym kraju?', a: 'Nie. Koszty i czasy dostawy różnią się w zależności od kraju, więc zawsze sprawdzaj opcje dla swojego kraju.' },
      { q: 'Gdzie znajdę dokładny koszt?', a: 'Sprawdź opcje wysyłki pokazywane przy kasie dla Twojego zamówienia lub skorzystaj z dostępnego kalkulatora.' },
    ],
  },
  es: {
    title: 'Costo de envío de USFans: guía completa de entrega',
    description:
      'Entiende los factores que influyen en el costo de envío de USFans, del destino y el peso al método y los servicios extra.',
    tag: 'Envío',
    intro: [
      'El costo de envío es una de las primeras cosas que la gente quiere saber antes de pedir. El precio exacto suele depender de varios factores, así que una cifra fija sin fuente actual puede confundir.',
      'Esta guía explica qué afecta el costo de entrega y cómo pensarlo, para que planifiques mejor y evites sorpresas al pagar.',
    ],
    sections: [
      {
        title: 'Factores que afectan el costo de envío',
        paragraphs: [
          'El destino, el peso del paquete, su tamaño y el método de envío elegido son los mayores impulsores del costo. Los servicios extra, como entrega rápida o seguro, también cambian el total.',
          'Como estos factores se combinan, dos pedidos parecidos pueden tener precios de entrega muy distintos.',
        ],
      },
      {
        title: 'Cómo estimar tu costo',
        paragraphs: [
          'La forma más fiable es revisar las opciones de envío que aparecen al pagar para tu país y paquete. Usa cualquier calculadora disponible en lugar de cifras viejas o copiadas.',
          'Ten en cuenta que los pedidos internacionales pueden incluir cargos adicionales o aduanas que no forman parte del precio base de envío.',
        ],
      },
      {
        title: 'Tiempo de entrega vs costo',
        paragraphs: [
          'Los métodos más rápidos suelen costar más, mientras que las opciones estándar son más baratas pero más lentas. Elige según cuánto tardes en necesitar el paquete.',
          'Confirma siempre el tiempo de entrega estimado para tu región antes de finalizar un pedido.',
        ],
      },
    ],
    faq: [
      { q: '¿Qué afecta el costo de envío de USFans?', a: 'El destino, el peso, el tamaño del paquete, el método de envío y los servicios extra influyen en el precio final.' },
      { q: '¿El envío es igual en todos los países?', a: 'No. Los costos y tiempos varían según el destino, así que revisa siempre las opciones para tu país.' },
      { q: '¿Dónde encuentro el costo exacto?', a: 'Revisa las opciones de envío que aparecen al pagar para tu pedido o usa una calculadora disponible.' },
    ],
  },
  fr: {
    title: 'Frais d’expédition USFans : guide complet de livraison',
    description:
      'Comprenez les facteurs qui influencent les frais d’expédition USFans, de la destination et du poids à la méthode et aux services supplémentaires.',
    tag: 'Expédition',
    intro: [
      'Les frais d’expédition sont l’une des premières choses que l’on veut connaître avant de commander. Le prix exact dépend généralement de plusieurs facteurs, donc un chiffre fixe sans source actuelle peut induire en erreur.',
      'Ce guide explique ce qui influence le coût de livraison et comment y réfléchir, pour mieux planifier et éviter les surprises au paiement.',
    ],
    sections: [
      {
        title: 'Facteurs qui influencent les frais d’expédition',
        paragraphs: [
          'La destination, le poids du colis, sa taille et la méthode choisie sont les principaux moteurs du coût. Les services supplémentaires comme la livraison rapide ou l’assurance changent aussi le total.',
          'Comme ces facteurs se combinent, deux commandes similaires peuvent avoir des frais de livraison très différents.',
        ],
      },
      {
        title: 'Comment estimer vos frais',
        paragraphs: [
          'Le plus fiable est de consulter les options d’expédition affichées au paiement pour votre pays et votre colis. Utilisez un calculateur disponible plutôt que des chiffres anciens ou copiés.',
          'Notez que les commandes internationales peuvent inclure des frais ou droits de douane supplémentaires hors du prix de base.',
        ],
      },
      {
        title: 'Délai de livraison vs coût',
        paragraphs: [
          'Les méthodes plus rapides coûtent souvent plus cher, tandis que les options standard sont moins chères mais plus lentes. Choisissez selon la rapidité dont vous avez besoin.',
          'Confirmez toujours le délai estimé pour votre région avant de finaliser une commande.',
        ],
      },
    ],
    faq: [
      { q: 'Qu’est-ce qui influence les frais d’expédition USFans ?', a: 'La destination, le poids, la taille du colis, la méthode d’expédition et les services supplémentaires influencent le prix final.' },
      { q: 'L’expédition est-elle identique dans tous les pays ?', a: 'Non. Les coûts et délais varient selon la destination, alors vérifiez toujours les options pour votre pays.' },
      { q: 'Où trouver le coût exact ?', a: 'Consultez les options d’expédition affichées au paiement pour votre commande ou utilisez un calculateur disponible.' },
    ],
  },
};